import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { BookOpen, Search, Download, Calendar, User, MapPin } from 'lucide-react';
import { format } from 'date-fns';

interface StorySubmission {
  id: string;
  category: string;
  title: string;
  content: string;
  author_name: string;
  author_location: string;
  author_role: string;
  consent_publication: boolean;
  status: string;
  created_at: string;
}

export default function AdminFormsStories() {
  const [stories, setStories] = useState<StorySubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  useEffect(() => {
    fetchStories();
  }, []);

  // Support drill-down via query params like ?category=&q=
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const category = params.get('category');
      const q = params.get('q');
      if (category) setCategoryFilter(category);
      if (q) setSearchTerm(q);
    } catch {}
  }, []);


  const fetchStories = async () => {
    try {
      const { data, error } = await supabase.rpc('list_story_submissions');

      if (error) throw error;
      
      // The RPC now returns the array directly
      const result = data as any;
      console.log('Raw stories result:', result);
      
      let processedStories = [];
      if (Array.isArray(result)) {
        processedStories = result;
      } else if (typeof result === 'string') {
        try {
          processedStories = JSON.parse(result);
        } catch (e) {
          console.error('Error parsing JSON result:', e);
          processedStories = [];
        }
      }
      
      setStories(processedStories || []);
    } catch (error) {
      console.error('Error fetching story submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredStories = stories.filter(story => {
    const matchesSearch = 
      story.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      story.author_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      story.content.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = categoryFilter === 'all' || story.category === categoryFilter;
    
    return matchesSearch && matchesCategory;
  });

  const getStatusBadge = (status: string) => {
    const variants = {
      'pending': 'destructive',
      'approved': 'default',
      'published': 'default',
      'rejected': 'secondary'
    } as const;
    
    return <Badge variant={variants[status as keyof typeof variants] || 'secondary'}>{status}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Story Submissions</h1>
          <p className="text-muted-foreground">
            Manage community story submissions and publications
          </p>
        </div>
        <Button>
          <Download className="mr-2 h-4 w-4" />
          Export
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Stories</CardTitle>
            <BookOpen className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stories.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">This Week</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stories.filter(s => new Date(s.created_at) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)).length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Review</CardTitle>
            <Badge variant="destructive" className="h-4 w-4 p-0"></Badge>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stories.filter(s => s.status === 'pending').length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Published</CardTitle>
            <BookOpen className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stories.filter(s => s.status === 'published').length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filter Stories</CardTitle>
          <CardDescription>Search and filter story submissions</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by title, author, or content..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="success_story">Success Story</SelectItem>
                <SelectItem value="challenge_overcome">Challenge Overcome</SelectItem>
                <SelectItem value="family_journey">Family Journey</SelectItem>
                <SelectItem value="caregiver_story">Caregiver Story</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Stories Table */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Submissions ({filteredStories.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title & Category</TableHead>
                <TableHead>Author</TableHead>
                <TableHead>Content Preview</TableHead>
                <TableHead>Consent</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredStories.map((story) => (
                <TableRow key={story.id}>
                  <TableCell className="font-medium">
                    <div className="space-y-1">
                      <div className="font-medium">{story.title}</div>
                      <div className="text-sm text-muted-foreground">{story.category}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="flex items-center">
                        <User className="mr-1 h-3 w-3" />
                        {story.author_name}
                      </div>
                      <div className="flex items-center text-sm text-muted-foreground">
                        <MapPin className="mr-1 h-3 w-3" />
                        {story.author_location}
                      </div>
                      <div className="text-sm text-muted-foreground">{story.author_role}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="max-w-xs truncate" title={story.content}>
                      {story.content}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={story.consent_publication ? 'default' : 'destructive'}>
                      {story.consent_publication ? 'Yes' : 'No'}
                    </Badge>
                  </TableCell>
                  <TableCell>{getStatusBadge(story.status)}</TableCell>
                  <TableCell>
                    {format(new Date(story.created_at), 'MMM d, yyyy')}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {filteredStories.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              No stories found matching your criteria
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}